/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  List,
  ListItem,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";

const InputWithDropdown = ({
  placeholder,
  fetchFunction,
  setSelected,
  setDetails,
  name,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Fetch all results on focus
  const handleFocus = async () => {
    setIsOpen(true);
    if (allResults.length > 0) {
      // Already fetched — just show them
      setResults(allResults);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchFunction(""); // empty string = fetch all
      setAllResults(data || []);
      setResults(data || []);
    } catch (error) {
      console.error("Error fetching all results:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter locally as user types
  useEffect(() => {
    if (!query.trim()) {
      setResults(allResults);
      return;
    }

    const lower = query.toLowerCase();
    const filtered = allResults.filter(
      (item) =>
        item.name?.toLowerCase().includes(lower) ||
        item.email?.toLowerCase().includes(lower) ||
        item.uniqueId?.toLowerCase().includes(lower)
    );

    // If no local match, do a fresh server fetch for the query
    if (filtered.length === 0) {
      const timer = setTimeout(async () => {
        setLoading(true);
        try {
          const data = await fetchFunction(query);
          setResults(data || []);
        } catch (error) {
          console.error("Error fetching filtered results:", error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 400);
      return () => clearTimeout(timer);
    }

    setResults(filtered);
  }, [query, allResults]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset when ownerType changes (fetchFunction reference changes)
  useEffect(() => {
    setAllResults([]);
    setResults([]);
    setQuery("");
    setIsOpen(false);
  }, [fetchFunction]);

  const handleClick = (item) => {
    setSelected({ target: { name, value: item.id } });
    setDetails(item);
    setQuery(item.name); // show selected name in input
    setResults([]);
    setAllResults([]);
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={containerRef}>
      <Input
        placeholder={placeholder}
        value={query}
        name={name}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={handleFocus}
        autoComplete="off"
        _placeholder={{ fontSize: "sm", color: "#90A3BF" }}
        variant="filled"
        borderRadius="lg"
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
        w="100%"
        h="56px"
      />

      {isOpen && (loading || results.length > 0) && (
        <Box
          bg="white"
          boxShadow="lg"
          mt={1}
          p={2}
          borderRadius="md"
          zIndex={9999}
          position="absolute"
          w="100%"
          maxH="220px"
          overflowY="auto"
          border="1px solid"
          borderColor="gray.200"
        >
          <List>
            {loading ? (
              <ListItem p={2}>
                <Spinner size="sm" />
              </ListItem>
            ) : results.length === 0 ? (
              <ListItem p={3}>
                <Text fontSize="sm" color="gray.500">No results found</Text>
              </ListItem>
            ) : (
              results.map((item, index) => (
                <ListItem
                  key={item.id + index}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: "gray.100", cursor: "pointer" }}
                  onClick={() => handleClick(item)}
                >
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" fontSize="sm">
                      {item.name}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {item.email}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      ID: {item.uniqueId}
                    </Text>
                  </VStack>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default InputWithDropdown;